<template>
    <application-layout>
        <v-container>
            <h1 class="ml-2">Search A/R Tenant</h1>
            <v-form ref="searchForm">
                <v-container>
                    <div class="subheading mb-3">You must fill out at least one field to submit a search request.</div>
                    <v-layout>
                        <app-input
                            data-testid="ar-tenant-first-name-input"
                            class="ar-tenant-first-name-input mr-2"
                            v-model="tenant.first_name"
                            filled
                            label="First Name"
                            type="text"
                        ></app-input>
                        <app-input
                            data-testid="ar-tenant-last-name-input"
                            class="ar-tenant-last-name-input mx-1"
                            v-model="tenant.last_name"
                            filled
                            label="Last Name"
                            type="text"
                        ></app-input>
                        <app-input
                            data-testid="ar-tenant-phone-input"
                            class="ar-tenant-phone-input ml-2"
                            v-model="tenant.phone"
                            filled
                            label="Phone Number"
                            type="phone"
                        ></app-input>
                    </v-layout>
                    <v-layout>
                        <app-input
                            data-testid="ar-tenant-email-input"
                            class="ar-tenant-email-input mr-2"
                            v-model="tenant.email"
                            filled
                            label="Email"
                            type="email"
                        ></app-input>
                        <app-input
                            data-testid="ar-tenant-access-code-input"
                            class="ar-tenant-access-code-input mx-1"
                            v-model="tenant.access_code"
                            filled
                            label="Access Code"
                            type="text"
                        ></app-input>
                        <app-input
                            data-testid="ar-tenant-unit-number-input"
                            class="ar-tenant-unit-number-input ml-2"
                            v-model="tenant.unit_number"
                            filled
                            label="Unit Number"
                            type="number"
                        ></app-input>
                    </v-layout>
                    <v-layout>
                        <v-spacer/>
                        <v-btn
                            data-testid="ar-tenant-search-btn"
                            class="primary"
                            @click.native="findTenant"
                        >
                          Search
                        </v-btn>
                    </v-layout>
                    <v-layout v-if="searchLoading" justify-center class="mt-4">
                        <v-progress-circular
                            :size="60"
                            :width="6"
                            indeterminate
                            class="my-3"
                        />
                    </v-layout>
                    <v-layout v-else column>
                        <v-layout column v-if="hasSearched">
                            <span class="subheading mb-2">Search results:</span>
                            <v-divider/>
                            <v-data-table
                                :headers="headers"
                                :items="searchResults"
                                item-key="sUnitName"
                                :loading="loading"
                                no-data-text="No results found"
                                data-testid="tenant-search-data-table"
                            >
                                <template slot="item" slot-scope="props">
                                  <tr>
                                    <td data-testid="tenant-first-name">{{ props.item.sFName }}</td>
                                    <td data-testid="tenant-last-name">{{ props.item.sLName }}</td>
                                    <td data-testid="tenant-facility-code">{{ props.item.sLocationCode }}</td>
                                    <td data-testid="tenant-unit-number">{{ props.item.sUnitName }}</td>
                                    <td data-testid="tenant-past-due-amount">
                                        ${{ parseFloat(props.item.dcCurrentBalance).toFixed(2) }}
                                    </td>
                                    <td data-testid="tenant-auction-status">
                                        {{ props.item.bInAuction === "true" ? "Yes" : "No" }}
                                    </td>
                                    <td data-testid="tenant-auction-date">
                                        {{ props.item.auction_date ? formatDate(props.item.auction_date) : "N/A" }}
                                    </td>
                                    <td data-testid="tenant-email">{{ props.item.sEmail }}</td>
                                    <td>
                                        <v-btn
                                            data-testid="ar-tenant-select-btn"
                                            class="ar-tenant-select-btn primary"
                                            small
                                            :loading="routeLoading"
                                            @click.native="selectTenant(props.item)"
                                        >
                                          Select
                                        </v-btn>
                                    </td>
                                  </tr>
                                </template>
                            </v-data-table>
                        </v-layout>
                    </v-layout>
                </v-container>
            </v-form>
        </v-container>
    </application-layout>
</template>

<script>
import ApplicationLayout from "@/layouts/ApplicationLayout";
import moment from "moment";
import router from "@/router";

export default {
  name: "ARTenantSearch",
  components: {
    ApplicationLayout
  },
  data() {
    return {
      hasSearched: false,
      loading: false,
      searchLoading: false,
      routeLoading: false,
      tenant: {
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        access_code: "",
        unit_number: ""
      },
      searchResults: [],
      headers: [
        {
          text: "First Name",
          value: "sFName"
        },
        {
          text: "Last Name",
          value: "sLName"
        },
        {
          text: "Facility",
          value: "sLocationCode"
        },
        {
          text: "Unit #",
          value: "sUnitName"
        },
        {
          text: "Past Due $",
          value: "dcCurrentBalance"
        },
        {
          text: "In Auction",
          value: "bInAuction"
        },
        {
          text: "Auction Date",
          value: "bAuctionDate"
        },
        {
          text: "Email",
          value: "sEmail"
        },
        {
          text: "Actions...",
          sortable: false,
          value: "actions",
          width: "250px"
        }
      ]
    };
  },

  methods: {
    formatDate(date) {
      return moment(date).format("MM/DD/YYYY");
    },

    findTenant() {
      this.searchLoading = true;
      let payload = { tenant: this.tenant };
      this.$store.dispatch("globalFindTenant", payload)
        .then(resp => {
          this.searchResults = resp.data.results;
          this.searchLoading = false;
          this.hasSearched = true;
        })
        .catch(err => {
          this.searchLoading = false;
          this.$store.dispatch("handleError", err);
        });
    },

    selectTenant(tenant) {
      this.routeLoading = true;
      this.$store.dispatch("getFacility", tenant.sLocationCode)
        .then(resp => {
          router.push({
            name: "arTenant",
            params: {
              tenantId: tenant.TenantID,
              facilityId: resp.data.facility.id
            }
          }).catch(() => {});
          this.$store.commit("changingTenant", true);
          this.routeLoading = false;
        })
        .catch(err => {
          this.routeLoading = false;
          this.$store.dispatch("handleError", err);
        });
    }
  }
};
</script>
